import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LiveKitRoom, RoomAudioRenderer, GridLayout, ParticipantTile, ControlBar, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import '@livekit/components-styles';
import { Box, Spinner, Text, Center, Button, useToast, VStack } from '@chakra-ui/react';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import io from 'socket.io-client';

import CustomMeetingLayout from './CustomMeetingLayout';

function MyVideoConference({ user }) {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Filter tracks: students can only see instructor/admin tiles (camera and screen shares)
  const filteredTracks = user?.role === 'student'
    ? tracks.filter((t) => {
        try {
          const meta = JSON.parse(t.participant.metadata || '{}');
          return meta.role === 'admin' || meta.role === 'instructor';
        } catch (e) {
          return false;
        }
      })
    : tracks;

  return (
    <div className="lk-video-conference" data-lk-theme="default" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="lk-video-conference-inner" style={{ flexGrow: 1, overflow: 'hidden' }}>
        <GridLayout tracks={filteredTracks}>
          <ParticipantTile />
        </GridLayout>
      </div>
      <ControlBar />
    </div>
  );
}

export default function MeetingRoom() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [error, setError] = useState(null);
  const [isWaiting, setIsWaiting] = useState(false);
  const [socket, setSocket] = useState(null);
  const { user } = useContext(AuthContext);
  const toast = useToast();

  const livekitUrl = process.env.REACT_APP_LIVEKIT_URL || "ws://localhost:7880";
  // Fallback to localhost if no env var, assuming standard local dev port 5000
  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Get secretKey from navigation state (passed from MeetingsPage)
  const secretKey = location.state?.secretKey || "";

  useEffect(() => {
    if (!user) return;

    const fetchToken = async (keyToUse = secretKey) => {
      try {
        const response = await axiosInstance.post(`/meetings/token`, {
          meetingId: roomName,
          secretKey: keyToUse
        });
        if (response.data.success) {
          setToken(response.data.token);
        } else {
          setError(response.data.error || "Failed to get token");
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || "خطأ في الانضمام إلى الحصة. تأكد من الرقم السري أو أن الحصة لا تزال نشطة.");
      }
    };

    const newSocket = io(backendUrl);
    setSocket(newSocket);

    if (user.role === 'student') {
      setIsWaiting(true);
      newSocket.emit("request_admission", { roomName, user });

      newSocket.on("admission_result", ({ approved, secretKey: hostProvidedKey }) => {
        if (approved) {
          setIsWaiting(false);
          fetchToken(hostProvidedKey);
        } else {
          setError("تم رفض طلب انضمامك من قبل المعلم.");
          setIsWaiting(false);
        }
      });
    } else {
      // Instructors join the socket room to receive admission requests
      newSocket.emit("join_meeting_room", { roomName, user });
      fetchToken();
    }

    return () => {
      newSocket.disconnect();
    };
  }, [roomName, secretKey, user, backendUrl]);

  if (error) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <Text fontSize="xl" color="red.400" mb={4}>{error}</Text>
        <Button colorScheme="blue" onClick={() => navigate(-1)}>العودة</Button>
      </Center>
    );
  }

  if (isWaiting) {
    return (
      <Center h="100vh" flexDirection="column" bg="gray.900" color="white">
        <Spinner size="xl" color="orange.400" mb={4} />
        <Text fontSize="xl">أنت في غرفة الانتظار...</Text>
        <Text fontSize="md" color="gray.400">يرجى الانتظار حتى يسمح لك المعلم بالدخول</Text>
      </Center>
    );
  }

  if (!token) {
    return (
      <Center h="100vh" bg="gray.900" color="white">
        <Spinner size="xl" color="teal.400" />
        <Text ml={4}>جاري الانضمام...</Text>
      </Center>
    );
  }

  return (
    <Box minH="100vh" w="100%" overflow="hidden" bg="#0B0E14" display="flex" alignItems="center" justifyContent="center">
      <Box w={{ base: "95%", lg: "70vw" }} h={{ base: "90vh", lg: "80vh" }} mx="auto" borderRadius="3xl" overflow="hidden" shadow="2xl" border="1px solid" borderColor="whiteAlpha.200" bg="black" position="relative">
        <LiveKitRoom
          video={true}
          audio={true}
          token={token}
          serverUrl={livekitUrl}
          onDisconnected={() => navigate(-1)}
          data-lk-theme="default"
          className="my-custom-livekit-room"
        >
          <MyVideoConference user={user} />
          {socket && <CustomMeetingLayout socket={socket} roomName={roomName} secretKey={secretKey} user={user} />}
          <RoomAudioRenderer />
        </LiveKitRoom>
      </Box>
    </Box>
  );
}
